<div class="box">
  <div class="box-row header mb-2">
    <app-header-bar [backRoute]="'/devices'" [title]="device?.uniqueIdentifier || 'lighttag.title'" [autoTranslateTitle]="device === undefined"></app-header-bar>
  </div>

  <app-loading-indicator [loading]="isLoading"></app-loading-indicator>

  @if (device) {
    <div class="card lighttag-hero-card mb-3">
      <div class="card-body">
        <div class="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-3">
          <div>
            <h5 class="card-title mb-1">{{ 'lighttag.overview.title' | translate }}</h5>
            <p class="text-muted mb-0">{{ 'lighttag.overview.subtitle' | translate }}</p>
          </div>
          <div class="d-flex flex-wrap align-items-center justify-content-end gap-2 lighttag-hero-badges">
            @if (headerPerformanceProfileKey; as profileKey) {
              <div class="badge rounded-pill text-bg-light d-inline-flex align-items-center gap-1">
                <span class="material-symbols-outlined" style="font-size:18px;line-height:1">speed</span>
                <span>{{ performanceProfileTranslationKey(profileKey) | translate }}</span>
              </div>
            }
            <div
              class="d-none d-md-flex align-items-center"
              [innerHTML]="device.isListening | toLightTagTypeBadge"
            ></div>
          </div>
        </div>

        <div class="row g-3">
          <div class="col-6 col-md-6 col-xl-3 order-2 order-md-1">
            <small class="text-muted text-uppercase lighttag-summary-label">{{ 'lighttag.serialNr' | translate }}</small>
            <div class="lighttag-summary-value">{{ device.uniqueIdentifier || '-' }}</div>
          </div>
          <div class="col-6 col-md-6 col-xl-3 order-1 order-md-2">
            <small class="text-muted text-uppercase lighttag-summary-label">{{ 'lighttag.lastLocation' | translate }}</small>
            <div class="lighttag-summary-value">{{ device.lastLocation || '-' }}</div>
          </div>
          <div class="col-6 col-md-6 col-xl-3">
            <small class="text-muted text-uppercase lighttag-summary-label">{{ 'lighttag.lastBatteryStatus' | translate }}</small>
            <div class="lighttag-summary-value">{{ device.lastBatteryStatus | lightTagBattery }}</div>
          </div>
          @if (isAdmin) {
            <div class="col-6 col-md-6 col-xl-3">
              <small class="text-muted text-uppercase lighttag-summary-label">{{ 'batteryPrediction.status' | translate }}</small>
              <div class="lighttag-summary-value">
                <app-battery-prediction-badge [prediction]="device.batteryPrediction"></app-battery-prediction-badge>
              </div>
            </div>
            <div class="col-6 col-md-6 col-xl-3">
              <small class="text-muted text-uppercase lighttag-summary-label">{{ 'batteryPrediction.expectedEndOfLife' | translate }}</small>
              <div class="lighttag-summary-value">
                {{ predictedEmptyAt ? (predictedEmptyAt | dateTime) : '-' }}
              </div>
            </div>
          }
          <div class="col-6 col-md-6 col-xl-3">
            <small class="text-muted text-uppercase lighttag-summary-label">{{ 'createdAt' | translate }}</small>
            <div class="lighttag-summary-value">{{ device.createdAt | dateTime }}</div>
          </div>
        </div>
      </div>
    </div>

    <div class="accordion" id="lighttag-detail-accordion">
      <div class="accordion-item">
        <h2 class="accordion-header" id="lighttag-activity-heading">
          <button
            class="accordion-button"
            [class.collapsed]="!isSectionOpen('activity')"
            type="button"
            (click)="toggleSection('activity')"
            [attr.aria-expanded]="isSectionOpen('activity')"
            aria-controls="lighttag-activity-body"
          >
            {{ 'lighttag.detailSections.activity' | translate }}
          </button>
        </h2>
        <div
          id="lighttag-activity-body"
          class="accordion-collapse collapse"
          [class.show]="isSectionOpen('activity')"
          aria-labelledby="lighttag-activity-heading"
        >
          <div class="accordion-body">
            <div class="row g-3">
              <div class="col-12 col-md-6">
                <h6 class="fw-bold mb-1">{{ 'lighttag.lastSeen' | translate }}</h6>
                <div [innerHTML]="device.lastSeen ? (device.lastSeen | timeAgo) : '-'"></div>
              </div>
              <div class="col-12 col-md-6">
                <h6 class="fw-bold mb-1">{{ 'lighttag.lastIlluminated' | translate }}</h6>
                <div [innerHTML]="device.lastIlluminated ? (device.lastIlluminated | timeAgo) : '-'"></div>
              </div>
              <div class="col-12 col-md-6">
                <h6 class="fw-bold mb-1">{{ 'lighttag.listeningType' | translate }}</h6>
                <div [innerHTML]="device.isListening | toLightTagTypeBadge"></div>
              </div>
              <div class="col-12 col-md-6">
                <h6 class="fw-bold mb-1">{{ 'lastUpdatedAt' | translate }}</h6>
                <div>{{ device.lastUpdatedAt | dateTime }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="accordion-item">
        <h2 class="accordion-header" id="lighttag-aggregate-heading">
          <button
            class="accordion-button"
            [class.collapsed]="!isSectionOpen('aggregate')"
            type="button"
            (click)="toggleSection('aggregate')"
            [attr.aria-expanded]="isSectionOpen('aggregate')"
            aria-controls="lighttag-aggregate-body"
          >
            {{ 'lighttag.detailSections.aggregateData' | translate }}
          </button>
        </h2>
        <div
          id="lighttag-aggregate-body"
          class="accordion-collapse collapse"
          [class.show]="isSectionOpen('aggregate')"
          aria-labelledby="lighttag-aggregate-heading"
        >
          <div class="accordion-body">
            @if (hasAggregateData()) {
              <div class="row g-3">
                @if (hasDisplayValue(device.lastSeen)) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.lastSeen' | translate }}</h6>
                    <div [innerHTML]="device.lastSeen ? (device.lastSeen | timeAgo) : '-'"></div>
                  </div>
                }
                @if (hasDisplayValue(device.lastIlluminated)) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.lastIlluminated' | translate }}</h6>
                    <div [innerHTML]="device.lastIlluminated ? (device.lastIlluminated | timeAgo) : '-'"></div>
                  </div>
                }
                @if (hasDisplayValue(device.lastLocation)) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.lastLocation' | translate }}</h6>
                    <div>{{ device.lastLocation }}</div>
                  </div>
                }
                @if (hasDisplayValue(device.lastBatteryStatus)) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.lastBatteryStatus' | translate }}</h6>
                    <div>{{ device.lastBatteryStatus | lightTagBattery }}</div>
                  </div>
                }
                @if (isAdmin && device.batteryPrediction) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'batteryPrediction.status' | translate }}</h6>
                    <div>
                      <app-battery-prediction-badge [prediction]="device.batteryPrediction"></app-battery-prediction-badge>
                    </div>
                  </div>
                }
                @if (isAdmin) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'batteryPrediction.expectedEndOfLife' | translate }}</h6>
                    <div>
                      {{ predictedEmptyAt ? (predictedEmptyAt | dateTime) : '-' }}
                    </div>
                  </div>
                }
                @if (hasDisplayValue(device.isListening)) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.listeningType' | translate }}</h6>
                    <div [innerHTML]="device.isListening | toLightTagTypeBadge"></div>
                  </div>
                }
                @if (performanceModeDisplay; as performanceMode) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.performanceMode' | translate }}</h6>
                    @if (performanceMode.type === 'parameters') {
                      <div class="d-flex flex-column gap-1">
                        <div>
                          <span class="fw-semibold">{{ 'lighttag.performanceModeParameters.activeInterval' | translate }}:</span>
                          {{ performanceParameterValue(performanceMode.activeIntervalMs, 'ms') }}
                        </div>
                        <div>
                          <span class="fw-semibold">{{ 'lighttag.performanceModeParameters.activeWindow' | translate }}:</span>
                          {{ performanceParameterValue(performanceMode.activeWindowMs, 'ms') }}
                        </div>
                        <div>
                          <span class="fw-semibold">{{ 'lighttag.performanceModeParameters.passiveInterval' | translate }}:</span>
                          {{ performanceParameterValue(performanceMode.passiveIntervalMs, 'ms') }}
                        </div>
                        <div>
                          <span class="fw-semibold">{{ 'lighttag.performanceModeParameters.passiveWindow' | translate }}:</span>
                          {{ performanceParameterValue(performanceMode.passiveWindowMs, 'ms') }}
                        </div>
                        <div>
                          <span class="fw-semibold">{{ 'lighttag.performanceModeParameters.sleepAfterSeconds' | translate }}:</span>
                          {{ performanceParameterValue(performanceMode.sleepAfterSeconds, 's') }}
                        </div>
                      </div>
                    } @else {
                      <div>{{ performanceProfileTranslationKey(performanceMode.profileKey) | translate }}</div>
                    }
                  </div>
                }
                @if (hasDisplayValue(device.uptimeInDays)) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.uptimeInDays' | translate }}</h6>
                    <div>{{ device.uptimeInDays }}</div>
                  </div>
                }
                @if (hasDisplayValue(device.uptimeSinceLastCharge)) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.uptimeSinceLastCharge' | translate }}</h6>
                    <div>{{ device.uptimeSinceLastCharge }}</div>
                  </div>
                }
                @if (hasDisplayValue(device.ledOperationTime)) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.ledOperationTime' | translate }}</h6>
                    <div>{{ device.ledOperationTime }}</div>
                  </div>
                }
                @if (hasDisplayValue(device.numberOfConnections)) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.numberOfConnections' | translate }}</h6>
                    <div>{{ device.numberOfConnections }}</div>
                  </div>
                }
              </div>
            } @else {
              <p class="mb-0 text-muted fst-italic">
                {{ 'lighttag.emptyStates.aggregateData' | translate }}
              </p>
            }
          </div>
        </div>
      </div>

      <div class="accordion-item">
        <h2 class="accordion-header" id="lighttag-software-heading">
          <button
            class="accordion-button"
            [class.collapsed]="!isSectionOpen('software')"
            type="button"
            (click)="toggleSection('software')"
            [attr.aria-expanded]="isSectionOpen('software')"
            aria-controls="lighttag-software-body"
          >
            {{ 'lighttag.detailSections.softwareData' | translate }}
          </button>
        </h2>
        <div
          id="lighttag-software-body"
          class="accordion-collapse collapse"
          [class.show]="isSectionOpen('software')"
          aria-labelledby="lighttag-software-heading"
        >
          <div class="accordion-body">
            @if (hasSoftwareData()) {
              <div class="row g-3">
                @if (hasDisplayValue(device.firmwareVersion)) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.firmwareVersion' | translate }}</h6>
                    <div>{{ device.firmwareVersion }}</div>
                  </div>
                }
                @if (hasDisplayValue(device.hardwareVersion)) {
                  <div class="col-12 col-md-6">
                    <h6 class="fw-bold mb-1">{{ 'lighttag.hardwareVersion' | translate }}</h6>
                    <div>{{ device.hardwareVersion }}</div>
                  </div>
                }
              </div>
            } @else {
              <p class="mb-0 text-muted fst-italic">
                {{ 'lighttag.emptyStates.softwareData' | translate }}
              </p>
            }
          </div>
        </div>
      </div>

      @if (isAdmin) {
        <div class="accordion-item">
          <h2 class="accordion-header" id="lighttag-history-heading">
            <button
              class="accordion-button"
              [class.collapsed]="!isSectionOpen('history')"
              type="button"
              (click)="toggleSection('history')"
              [attr.aria-expanded]="isSectionOpen('history')"
              aria-controls="lighttag-history-body"
            >
              {{ 'deviceHistory.title' | translate }}
            </button>
          </h2>
          <div
            id="lighttag-history-body"
            class="accordion-collapse collapse"
            [class.show]="isSectionOpen('history')"
            aria-labelledby="lighttag-history-heading"
          >
            <div class="accordion-body">
              <app-device-history-list [historyItems]="historyItems"></app-device-history-list>
            </div>
          </div>
        </div>
      }
    </div>
  }

  <app-error [error]="error"></app-error>
</div>
